import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import MoleculeLoader from '../components/MoleculeLoader';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthCallbackPage = () => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Extract session_id from URL hash
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        toast.error('Authentication failed');
        navigate('/auth', { replace: true });
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const response = await fetch(`${API_URL}/api/auth/google/callback`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          
          body: JSON.stringify({ session_id: sessionId })
        });

        // Store ok status before reading body (Safari compatibility)
        const isOk = response.ok;
        
        let data;
        try {
          const text = await response.text();
          data = text ? JSON.parse(text) : {};
        } catch (parseError) {
          console.error('Auth callback parse error:', parseError);
          throw new Error(isOk ? 'Could not complete sign-in. Please try again.' : 'Sign-in service is temporarily unavailable. Please try again.');
        }

        if (!isOk) {
          throw new Error(data?.detail || 'Authentication failed. Please try again.');
        }
        
        // Store token in localStorage for Authorization header fallback
        if (data.token) {
          localStorage.setItem('authToken', data.token);
        }
        
        setUser(data.user);
        toast.success(`Welcome, ${data.user.name}!`);
        
        // Navigate to dashboard with user data to prevent race condition
        navigate('/dashboard', { replace: true, state: { user: data.user } });
      } catch (error) {
        console.error('Auth callback error:', error);
        // Ensure we only show string messages in toast
        const errorMessage = error instanceof Error ? error.message : 'Authentication failed. Please try again.';
        toast.error(String(errorMessage));
        navigate('/auth', { replace: true });
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <MoleculeLoader size={88} label="Completing sign in…" />
    </div>
  );
};

export default AuthCallbackPage;
